/**
 * StepLockControl
 *
 * Canonical interaction component for locking/unlocking a set of objects
 * on the Track page (participants, criteria, candidate pool, etc.).
 *
 * State model:
 *   OPEN   → user can edit freely; conditions not yet satisfied
 *   READY  → conditions satisfied but set is not frozen
 *   LOCKED → set frozen, edits disabled
 */

import { Lock, Unlock, CheckCircle2, Circle, Loader2 } from 'lucide-react';
import type { CSSProperties } from 'react';

export type StepLockStatus = 'open' | 'ready' | 'locked';

export interface StepLockControlProps {
  /** Section title, e.g. "Participation" */
  title: string;
  /** Optional description shown below the title */
  description?: string;
  /** Current state derived from conditionsMet + locked */
  status: StepLockStatus;
  /** Whether all prerequisites for locking are satisfied */
  conditionsMet: boolean;
  /** Lock the set */
  onLock: () => void;
  /** Unlock the set (omit to disallow unlocking) */
  onUnlock?: () => void;
  /** Show a spinner on the lock/unlock button */
  busy?: boolean;
  /** When true, the lock/unlock action is not available (e.g. no iteration exists yet) */
  disabled?: boolean;
  /** Tooltip when disabled */
  disabledReason?: string;
  /** When set, the Unlock button is disabled and shows this reason (the caller may not unlock). */
  unlockDisabledReason?: string;
  /** The unlock button's name when unlocking does more than unlock (e.g. "New version"). */
  unlockLabel?: string;
  /**
   * When set, the person may neither lock nor unlock: no button is drawn, and
   * this line says what the act needs and who holds it (build contract §2.8).
   */
  readOnlyNote?: string;
}

const STATUS_CONFIG = {
  open: { color: 'var(--text-dim)', tint: 'var(--bg-raised)', label: 'Open' },
  ready: { color: 'var(--mustard)', tint: 'rgba(212,160,23,0.12)', label: 'Ready to lock' },
  locked: { color: 'var(--green)', tint: 'rgba(58,125,86,0.12)', label: 'Locked' },
} as const;

const lockLabel: CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: '0.6rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

export function StepLockControl({
  title,
  description,
  status,
  conditionsMet,
  onLock,
  onUnlock,
  busy = false,
  disabled = false,
  disabledReason,
  unlockDisabledReason,
  unlockLabel = 'Unlock',
  readOnlyNote,
}: StepLockControlProps) {
  const config = STATUS_CONFIG[status];
  const canLock = conditionsMet && !disabled;

  return (
    <div data-step-lock={title} style={{ border: '1px solid var(--rule)', background: 'var(--bg-raised)', borderRadius: '2px', padding: '12px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
        {/* Left: status indicator + label */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: 0 }}>
          {status === 'open' ? (
            <Circle size={18} style={{ color: config.color, flexShrink: 0 }} />
          ) : (
            <CheckCircle2 size={18} style={{ color: config.color, flexShrink: 0 }} />
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontFamily: 'var(--sans)', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-pri)' }}>{title}</span>
              <span className={status === 'locked' ? 'dark:!text-[var(--text-ter)]' : undefined} style={{ ...lockLabel, display: 'inline-flex', alignItems: 'center', gap: '4px', borderRadius: '2px', background: config.tint, padding: '2px 8px', color: config.color }}>
                {status === 'locked' && <Lock size={10} />}
                {config.label}
              </span>
            </div>
            {description && (
              <p style={{ fontFamily: 'var(--sans)', fontSize: '0.75rem', color: 'var(--text-sec)', marginTop: '2px' }}>{description}</p>
            )}
          </div>
        </div>

        {/* Right: action button */}
        <div style={{ flexShrink: 0, maxWidth: readOnlyNote ? '45%' : undefined }}>
          {readOnlyNote ? (
            <p style={{ fontFamily: 'var(--sans)', fontSize: '0.72rem', color: 'var(--text-ter)', textAlign: 'right' }}>{readOnlyNote}</p>
          ) : status === 'locked' ? (
            onUnlock ? (
              <button
                onClick={onUnlock}
                disabled={busy || !!unlockDisabledReason}
                title={unlockDisabledReason}
                style={{
                  ...lockLabel,
                  display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
                  color: 'var(--text-sec)', background: 'none', border: '1px solid var(--rule)', borderRadius: '2px',
                  cursor: busy || unlockDisabledReason ? 'not-allowed' : 'pointer', opacity: busy || unlockDisabledReason ? 0.5 : 1,
                }}
              >
                {busy ? <Loader2 className="animate-spin" size={12} /> : <Unlock size={12} />}
                {unlockLabel}
              </button>
            ) : null
          ) : (
            <button
              onClick={onLock}
              disabled={busy || !canLock}
              title={
                disabled
                  ? disabledReason
                  : !conditionsMet
                  ? 'Conditions not yet met'
                  : 'Lock this set'
              }
              style={{
                ...lockLabel,
                display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px',
                color: canLock ? '#fff' : 'var(--text-dim)',
                background: canLock ? 'var(--mustard)' : 'var(--bg-raised)',
                border: canLock ? 'none' : '1px solid var(--rule)',
                borderRadius: '2px',
                cursor: busy || !canLock ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              {busy ? <Loader2 className="animate-spin" size={12} /> : <Lock size={12} />}
              Lock Set
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default StepLockControl;
